import { createContext, useEffect, useMemo, useCallback } from "react";
import axios from "../../utils/axios";
// settings
import actionTypes from "../../common/actionTypes";
import { DATABASE_STATUTES } from "../../common/constants";
// context
import useAuthContext from "../../hooks/useAuthContext";
// reducers
import notificationReducer from "../../reducers/V2/notificationReducer";
// hooks
import useCustomReducer from "../../hooks/useCustomReducer";

const NOTIFICATION_SETTINGS_API = "/v2/notification-settings";

const initialState = {
  notifications: [],
  currentNotification: null,
  status: DATABASE_STATUTES.NOT_LOADED,
  error: null,
};

const NotificationContext = createContext({
  ...initialState,
});

const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useCustomReducer(notificationReducer, initialState);
  const { user } = useAuthContext();

  const fetchNotifications = useCallback(async () => {
    try {
      dispatch({ type: actionTypes.FETCH_NOTIFICATIONS });

      const response = await axios.get(NOTIFICATION_SETTINGS_API);
      const notifications = response.data.data.notificationSettings;

      dispatch({
        type: actionTypes.FETCH_NOTIFICATIONS_SUCCESS,
        payload: {
          notifications: notifications,
        },
      });
    } catch (error) {
      dispatch({
        type: actionTypes.FETCH_NOTIFICATIONS_FAILURE,
        error: error.message || "Failed to fetch notifications",
      });
    }
  }, [dispatch]);

  const fetchNotification = useCallback(
    async (notificationId) => {
      try {
        dispatch({ type: actionTypes.FETCH_NOTIFICATION });

        const response = await axios.get(`${NOTIFICATION_SETTINGS_API}/${notificationId}`);
        const notification = response.data;

        dispatch({
          type: actionTypes.FETCH_NOTIFICATION_SUCCESS,
          payload: {
            notification,
          },
        });
      } catch (error) {
        console.error("Error fetching notification:", error);
        dispatch({
          type: actionTypes.FETCH_NOTIFICATION_FAILURE,
          error: error.response?.data?.message || error.message || "Failed to fetch notification",
        });
      }
    },
    [dispatch]
  );

  const updateNotification = useCallback(
    async (notificationId, updates) => {
      try {
        dispatch({ type: actionTypes.UPDATE_NOTIFICATION });

        const response = await axios.put(`${NOTIFICATION_SETTINGS_API}/${notificationId}`, updates);
        const updatedNotification = response.data;

        dispatch({
          type: actionTypes.UPDATE_NOTIFICATION_SUCCESS,
          payload: {
            notification: updatedNotification,
          },
        });
      } catch (error) {
        dispatch({
          type: actionTypes.UPDATE_NOTIFICATION_FAILURE,
          error: error.message || "Failed to update notification",
        });
      }
    },
    [dispatch]
  );

  const sendTestEmail = useCallback(async (notificationId, testEmail) => {
    try {
      await axios.post(`${NOTIFICATION_SETTINGS_API}/${notificationId}/send-test`, {
        testEmail,
      });

      return {
        success: true,
      };
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to send test email";
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, []);

  // Auto-fetch notifications when component mounts
  useEffect(() => {
    if (user?.domain) {
      fetchNotifications();
    }
  }, [user?.domain, fetchNotifications]);

  const memoizedState = useMemo(() => ({
    ...state,
    fetchNotifications,
    fetchNotification,
    updateNotification,
    sendTestEmail,
  }), [state, fetchNotifications, fetchNotification, updateNotification, sendTestEmail]);

  return <NotificationContext.Provider value={memoizedState}>{children}</NotificationContext.Provider>;
};

export { NotificationContext, NotificationProvider };
